import React, { useState, useEffect, useCallback } from 'react';
import { X, Phone, Mail, User, CheckCircle, Loader2, Gift, Clock, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'sleepApneaPopupDismissed';
const VISIT_COUNT_KEY = 'siteVisitCount';
const LAST_VISIT_KEY = 'lastVisitTimestamp';
const DISMISS_DAYS = 3;
const SHOW_DELAY_MS = 8000;
const FORMSPREE_ID = 'mvkpjgoe';

// Google Analytics event tracking helper
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
    console.log(`[GA4] Event tracked: ${eventName}`, params);
  }
};

// Different messaging for new vs returning visitors
const popupContent = {
  newVisitor: {
    badge: 'Free Consultation',
    headline: 'Struggling with Snoring or Sleep Apnea?',
    subheadline: "Get a complimentary phone consultation with Dr. Yang's team. We'll discuss your symptoms and treatment options.",
    cta: 'Request Free Consultation',
    gradient: 'from-cyan-600 to-cyan-800',
    variantId: 'A', // For A/B testing identification
  },
  returning: {
    badge: 'Welcome Back!',
    headline: 'Still Researching Sleep Apnea Solutions?',
    subheadline: "We noticed you're still exploring options. Let's chat — Dr. Yang has helped 500+ patients just like you find relief without CPAP.",
    cta: 'Schedule My Call',
    gradient: 'from-purple-600 to-indigo-800',
    variantId: 'B',
  },
  frequentVisitor: {
    badge: 'Special Offer',
    headline: 'Ready to Take the Next Step?',
    subheadline: "You've done your research. Book your consultation today and receive a complimentary sleep apnea screening valued at $150.",
    cta: 'Claim My Free Screening',
    gradient: 'from-amber-500 to-orange-600',
    variantId: 'C',
  },
};

const LeadCapturePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', email: '', phone: '' });
  const [visitorType, setVisitorType] = useState('newVisitor');
  const [triggerType, setTriggerType] = useState('timer'); // 'timer' or 'exit_intent'

  // Track popup view when it becomes visible
  const trackPopupView = useCallback((type, trigger) => {
    const content = popupContent[type];
    trackEvent('popup_view', {
      event_category: 'Lead Capture',
      event_label: `Variant ${content.variantId} - ${type}`,
      popup_variant: content.variantId,
      visitor_type: type,
      trigger_type: trigger,
      visit_count: localStorage.getItem(VISIT_COUNT_KEY) || '1',
    });
  }, []);

  useEffect(() => {
    // Don't show global popup on quiz page - it has its own exit intent
    if (window.location.pathname === '/sleep-apnea-quiz') return;
    
    // Track visit count and determine visitor type
    const visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0') + 1;
    localStorage.setItem(VISIT_COUNT_KEY, visitCount.toString());
    localStorage.setItem(LAST_VISIT_KEY, Date.now().toString());

    // Determine visitor type based on visit count
    let determinedType = 'newVisitor';
    if (visitCount >= 4) {
      determinedType = 'frequentVisitor';
    } else if (visitCount >= 2) {
      determinedType = 'returning';
    }
    setVisitorType(determinedType);

    // Check if popup was recently dismissed
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) return;
    }

    // Timed trigger
    const timer = setTimeout(() => {
      setTriggerType('timer');
      setIsVisible(true);
      trackPopupView(determinedType, 'timer');
    }, SHOW_DELAY_MS);

    // Exit-intent trigger (mouse leaving viewport)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 5) {
        setTriggerType('exit_intent');
        setIsVisible(true);
        trackPopupView(determinedType, 'exit_intent');
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [trackPopupView]);

  const handleDismiss = () => {
    const content = popupContent[visitorType];
    
    // Track popup dismissal
    trackEvent('popup_dismiss', {
      event_category: 'Lead Capture',
      event_label: `Variant ${content.variantId} - ${visitorType}`,
      popup_variant: content.variantId,
      visitor_type: visitorType,
      trigger_type: triggerType,
    });
    
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const content = popupContent[visitorType];
    
    // Track form start (user clicked submit)
    trackEvent('popup_form_submit', {
      event_category: 'Lead Capture',
      event_label: `Variant ${content.variantId} - ${visitorType}`,
      popup_variant: content.variantId,
      visitor_type: visitorType,
      trigger_type: triggerType,
    });
    
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: `New Sleep Apnea Lead - ${visitorType === 'frequentVisitor' ? 'HOT LEAD' : visitorType === 'returning' ? 'Returning Visitor' : 'New Visitor'}`,
          'Lead Type': 'Sleep Apnea Consultation Request',
          'Visitor Type': visitorType,
          'Popup Variant': content.variantId,
          'Trigger Type': triggerType,
          'Visit Count': localStorage.getItem(VISIT_COUNT_KEY),
          'First Name': formData.firstName,
          'Email': formData.email,
          'Phone': formData.phone,
          'Source Page': window.location.pathname,
          'Popup Version': content.badge,
        }),
      });
      
      // Track successful conversion
      trackEvent('popup_conversion', {
        event_category: 'Lead Capture',
        event_label: `Variant ${content.variantId} - ${visitorType}`,
        popup_variant: content.variantId,
        visitor_type: visitorType,
        trigger_type: triggerType,
        value: visitorType === 'frequentVisitor' ? 150 : visitorType === 'returning' ? 100 : 50, // Estimated lead value
      });
      
      // Also track as a Google Ads conversion-friendly event
      trackEvent('generate_lead', {
        event_category: 'Lead Capture',
        currency: 'USD',
        value: visitorType === 'frequentVisitor' ? 150 : visitorType === 'returning' ? 100 : 50,
      });
      
      setIsSubmitted(true);
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      setIsSubmitted(true);
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  const content = popupContent[visitorType];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lead-capture-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10"
          data-testid="popup-close-button"
        >
          <X size={20} />
        </button>

        {!isSubmitted ? (
          <>
            {/* Header - Dynamic based on visitor type */}
            <div className={`bg-gradient-to-br ${content.gradient} px-8 pt-8 pb-6 text-white`}>
              <div className="flex items-center gap-2 mb-2">
                {visitorType === 'frequentVisitor' && <Gift size={16} className="text-amber-200" />}
                {visitorType === 'returning' && <Clock size={16} className="text-purple-200" />}
                <p className={`text-sm font-semibold uppercase tracking-wide ${
                  visitorType === 'frequentVisitor' ? 'text-amber-200' : 
                  visitorType === 'returning' ? 'text-purple-200' : 'text-cyan-200'
                }`}>
                  {content.badge}
                </p>
              </div>
              <h2 className="text-2xl font-bold leading-tight" data-testid="popup-headline">
                {content.headline}
              </h2>
              <p className={`text-sm mt-2 ${
                visitorType === 'frequentVisitor' ? 'text-amber-100' : 
                visitorType === 'returning' ? 'text-purple-100' : 'text-cyan-100'
              }`}>
                {content.subheadline}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4" data-testid="lead-capture-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    required
                    placeholder="Your first name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all text-sm"
                    data-testid="popup-firstname-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    required
                    placeholder="you@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all text-sm"
                    data-testid="popup-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-gray-400">(optional)</span></label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    placeholder="(916) 555-0123"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all text-sm"
                    data-testid="popup-phone-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 text-white ${
                  visitorType === 'frequentVisitor' 
                    ? 'bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300' 
                    : visitorType === 'returning'
                    ? 'bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400'
                    : 'bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400'
                }`}
                data-testid="popup-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    {content.cta}
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center">
                We respect your privacy. No spam, ever.
              </p>
            </form>
          </>
        ) : (
          /* Success State */
          <div className="px-8 py-12 text-center" data-testid="popup-success">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {visitorType === 'frequentVisitor' ? 'Your Free Screening is Reserved!' : 'Thank You!'}
            </h3>
            <p className="text-gray-600 mb-6">
              {visitorType === 'frequentVisitor' 
                ? "We'll call you within 24 hours to schedule your complimentary sleep apnea screening. Get ready to start sleeping better!"
                : "Our team will reach out shortly to discuss your sleep apnea concerns. We look forward to helping you sleep better."
              }
            </p>
            <button
              onClick={handleDismiss}
              className={`font-semibold px-6 py-2.5 rounded-lg transition-colors text-white ${
                visitorType === 'frequentVisitor' 
                  ? 'bg-amber-500 hover:bg-amber-600' 
                  : visitorType === 'returning'
                  ? 'bg-purple-600 hover:bg-purple-700'
                  : 'bg-cyan-600 hover:bg-cyan-700'
              }`}
            >
              Continue Browsing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCapturePopup;
